import { useQuery } from "@tanstack/react-query";
import { Navigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { routes } from "@/routes";

import type { PortfolioRouteStates } from "../portfolio/routes";

const SUPPORTED_CHAT_TYPES = ["single", "group"];

type Params = {
  p?: string;
  cid?: string;
  t?: string;
};

export function ChatsRedirect(): React.ReactNode {
  const [params] = useQueryParams<Params>();

  // Missed-chat links carry a chat ID for both private and group conversations.
  const chatId = params.cid;
  const projectId = params.p;
  const chatType = params.t;

  const selfAccountQueries = useSelfAccountQueries();

  const {
    data: linkedProjects,
    isPending: isPendingLinkedProjects,
    isError: isErrorLinkedProjects,
  } = useQuery(selfAccountQueries.listLinkedProjects());

  const isLoading = isPendingLinkedProjects;
  const isParamsMissing = !chatId || !projectId || !chatType || !SUPPORTED_CHAT_TYPES.includes(chatType);
  const isError = isErrorLinkedProjects;

  if (isParamsMissing || isError) {
    return <ErrorPage status={404} />;
  }

  if (isLoading) {
    return null;
  }

  const project = linkedProjects.find((x) => x.id === projectId);
  if (!project) {
    return <ErrorPage status={404} />;
  }

  if (linkedProjects.length > 1) {
    return (
      <Navigate
        replace
        to={routes.portfolio.chats()}
        state={
          {
            activeChatId: chatId,
            activeChatType: chatType === "single" ? "private" : "group",
          } satisfies PortfolioRouteStates["chats"]
        }
      />
    );
  }

  return <Navigate replace to={routes.chats.details({ slug: project.slug, id: chatId })} />;
}
